import { useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ClickAwayListener, Popper, useTheme } from '@mui/material';
import { PopupContextProvider, usePopupContext } from '@kc-console/common-ui/singleton';
import { useTranslation } from '@kc-console/core';

import {
  POPOVER_DATETIME_FORMAT,
  POPOVER_DATETIME_FORMAT_NO_SECONDS,
  POPOVER_DATETIME_RANGE_MASK,
  POPOVER_DATETIME_RANGE_MASK_NO_SECONDS,
} from '../constants';
import DatePickerInput from '../core/DatePickerInput';
import { useDatePickerLocale } from '../hooks/useDatePickerLocale';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { usePopoverState } from '../hooks/usePopoverState';
import {
  findSelectDropdown,
  formatDateRange,
  isDatePickerCalendar,
  isIgnoredElement,
  isMuiBackdrop,
  isPortalMountingIssue,
  isWithinElement,
} from '../utils';
import InlineDateTimeRangePicker from './InlineDateTimeRangePicker';

import type { ReactElement, Ref, RefObject } from 'react';
import type { PopperPlacementType } from '@mui/material';
import type { DatePickerInputProps, DatePickerLocale } from '../types';

export interface PopoverDateTimeRangePickerProps {
  /**
   * true이면 시작 일시와 종료 일시가 동일한 선택을 허용해 자동 +1일 보정을 건너뜁니다.
   * @default false
   */
  allowEqualDates?: boolean;

  /**
   * Popper를 렌더링할 기준 엘리먼트의 ref입니다.
   * 이 prop과 open prop이 함께 제공되면 Controlled 모드로 동작합니다.
   * Controlled 모드에서는 customInput은 무시되고 Popper만 렌더링됩니다.
   */
  anchorRef?: RefObject<HTMLElement | null>;

  /**
   * Popover 내부 input 직접 입력 시 자릿수(MM/dd) 및 minDate/maxDate 범위를 벗어나는 값을
   * 자동으로 경계값으로 보정합니다.
   * @default false
   */
  clampDateOutOfRange?: boolean;

  /**
   * 커스텀 trigger 엘리먼트를 지정합니다.
   * RenderProps 형태로 전달하여 open, close, isOpen, ref 등을 활용할 수 있습니다.
   * ref는 반드시 trigger 엘리먼트에 전달되어야 Popover가 올바른 위치에 표시됩니다.
   * Uncontrolled 모드에서만 사용됩니다.
   */
  customInput?: (props: {
    close: () => void;
    isOpen: boolean;
    open: () => void;
    ref: Ref<HTMLElement>;
    disabled?: boolean;
    error?: boolean;
    name?: string;
  }) => ReactElement;

  /**
   * disabled 상태를 설정합니다.
   */
  disabled?: boolean;

  /**
   * 종료 날짜를 지정합니다.
   */
  endDate?: Date | null;

  /**
   * error 상태를 설정합니다.
   */
  error?: boolean;

  /**
   * DatePickerInput에 적용할 Props입니다.
   * customTrigger가 제공되지 않을 때만 사용됩니다.
   */
  inputProps?: Omit<
    DatePickerInputProps,
    'value' | 'disabled' | 'error' | 'onClick' | 'onFocus' | 'onChange'
  >;

  /**
   * locale을 설정합니다.
   * @default 'ko'
   */
  locale?: DatePickerLocale;

  /**
   * 선택 가능한 최대 날짜를 지정합니다.
   */
  maxDate?: Date | null;

  /**
   * 선택 가능한 최소 날짜를 지정합니다.
   */
  minDate?: Date | null;

  /**
   * 요약 input과 내부 시작·종료 DateTimePicker에 전달할 기준 name입니다.
   */
  name?: string;

  /**
   * 날짜 범위 변경 시 호출되는 함수입니다.
   * 배열 형태로 [startDate, endDate]를 받습니다.
   */
  onChange?: (dates: [Date | null, Date | null]) => void;

  /**
   * Popper 열림/닫힘 상태 변경 시 호출됩니다.
   * Controlled 모드에서 권장됩니다.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Popper의 열림/닫힘 상태를 제어합니다.
   * 이 prop이 제공되면 Controlled 모드로 동작하며, anchorRef도 함께 필수입니다.
   */
  open?: boolean;

  /**
   * Popper의 위치를 설정합니다.
   * @default 'bottom'
   */
  placement?: PopperPlacementType;

  /**
   * 초 선택 필드 표시 여부를 설정합니다.
   * @default true
   */
  showSeconds?: boolean;

  /**
   * timezone(UTC offset) 정보를 표시할지 여부를 설정합니다.
   * @default false
   */
  showTimezone?: boolean;

  /**
   * 시작 날짜를 지정합니다.
   */
  startDate?: Date | null;
}

/**
 * Popover 형태로 동작하는 DateTime Range Picker 컴포넌트입니다.
 * Input(또는 custom trigger) + Popper + InlineDateTimeRangePicker 조합으로 구성됩니다.
 *
 * @example
 * ```tsx
 * // 기본 사용 (Uncontrolled)
 * <PopoverDateTimeRangePicker
 *   startDate={startDate}
 *   endDate={endDate}
 *   onChange={([start, end]) => {
 *     setStartDate(start);
 *     setEndDate(end);
 *   }}
 * />
 *
 * // Custom trigger 사용 (Uncontrolled)
 * <PopoverDateTimeRangePicker
 *   startDate={startDate}
 *   endDate={endDate}
 *   onChange={onChange}
 *   customInput={({ open, isOpen, ref }) => (
 *     <Button ref={ref} onClick={open}>
 *       {isOpen ? 'Close' : 'Open'} Date Range Picker
 *     </Button>
 *   )}
 * />
 *
 * // Controlled 모드 - 부모가 trigger와 open state를 관리
 * const [isOpen, setIsOpen] = useState(false);
 * const buttonRef = useRef<HTMLButtonElement>(null);
 *
 * <Button ref={buttonRef} onClick={() => setIsOpen(true)}>
 *   날짜 선택
 * </Button>
 * <PopoverDateTimeRangePicker
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   anchorRef={buttonRef}
 *   startDate={startDate}
 *   endDate={endDate}
 *   onChange={onChange}
 *   placement="bottom-end"
 * />
 * ```
 */
const PopoverDateTimeRangePicker = (props: PopoverDateTimeRangePickerProps) => {
  const {
    startDate,
    endDate,
    onChange,
    customInput,
    inputProps,
    disabled,
    error,
    locale: propLocale,
    minDate,
    maxDate,
    placement = 'bottom',
    showSeconds = true,
    showTimezone = false,
    anchorRef: externalAnchorRef,
    open: externalOpen,
    onOpenChange,
    clampDateOutOfRange,
    allowEqualDates,
    name,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const popupContext = usePopupContext();
  const locale = useDatePickerLocale(propLocale);
  const popperRef = useRef<HTMLDivElement>(null);
  const popperZIndex = popupContext?.zIndex ?? theme.zIndex.contentPopup;

  // Popover 상태 관리
  const { isControlled, open, anchorRef, openPopover, closePopover, internalAnchorRef } =
    usePopoverState({
      open: externalOpen,
      onOpenChange,
      anchorRef: externalAnchorRef,
      disabled,
    });

  // ESC 키로 닫기
  useEscapeKey({
    onEscape: closePopover,
    enabled: open,
  });

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;

    // 1. Portal 마운트 타이밍 이슈
    if (isPortalMountingIssue(target)) {
      return;
    }

    // 2. MUI Backdrop 무시
    if (isMuiBackdrop(target)) {
      return;
    }

    // 3. anchorEl(trigger) 클릭 무시
    if (isWithinElement(target, anchorRef?.current)) {
      return;
    }

    // 4. react-datepicker 캘린더 내부 클릭 무시
    if (isDatePickerCalendar(target)) {
      return;
    }

    // 5. DatePicker 내부의 Select 드롭다운만 무시
    const selectDropdown = findSelectDropdown(target);
    if (selectDropdown) {
      // Popper 내부인지 확인
      if (isWithinElement(selectDropdown as HTMLElement, popperRef.current)) {
        return; // DatePicker 내부 Select만 무시
      }
      // 외부 Select는 통과 → DatePicker 닫힘
    }

    // 6. react-datepicker-ignore-onclickoutside
    if (isIgnoredElement(target)) {
      return;
    }

    closePopover();
  };

  const handleChange = (dates: [Date | null, Date | null]) => {
    onChange?.(dates);
  };

  // showSeconds에 따라 dateFormat과 mask 선택
  const dateFormat = showSeconds ? POPOVER_DATETIME_FORMAT : POPOVER_DATETIME_FORMAT_NO_SECONDS;
  const rangeMask = showSeconds
    ? POPOVER_DATETIME_RANGE_MASK
    : POPOVER_DATETIME_RANGE_MASK_NO_SECONDS;

  const formattedValue: string = formatDateRange(startDate, endDate, dateFormat);

  return (
    <>
      {/* Controlled 모드에서는 trigger 렌더링 안 함 */}
      {!isControlled && (
        <>
          {customInput ? (
            customInput({
              open: openPopover,
              close: closePopover,
              isOpen: open,
              ref: internalAnchorRef,
              disabled,
              error,
              name,
            })
          ) : (
            <DatePickerInput
              {...inputProps}
              ref={internalAnchorRef as RefObject<HTMLDivElement>}
              value={formattedValue}
              mask={rangeMask}
              disabled={disabled}
              error={error}
              onClick={openPopover}
              readOnly
              name={name ?? inputProps?.name}
            />
          )}
        </>
      )}

      {/* Popper는 항상 렌더링 */}
      <StyledPopper
        ref={popperRef}
        open={open}
        anchorEl={anchorRef?.current}
        placement={placement}
        ownerZIndex={popperZIndex}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <StyledPaper>
            {/* Date range picker 내부 dropdown은 현재 popper와 같은 z-index(contentPopup=1295 또는 modalPopup=1410) 계층을 공유한다. */}
            <PopupContextProvider zIndex={popperZIndex}>
              <InlineDateTimeRangePicker
                name={name}
                startDate={startDate}
                endDate={endDate}
                locale={locale}
                onChange={handleChange}
                showSeconds={showSeconds}
                showTimezone={showTimezone}
                dateFormat={dateFormat}
                minDate={minDate}
                maxDate={maxDate}
                clampDateOutOfRange={clampDateOutOfRange}
                allowEqualDates={allowEqualDates}
                startLabel={t('common:common.label.date.started_at_timestamp')}
                endLabel={t('common:common.label.date.ended_at_timestamp')}
              />
            </PopupContextProvider>
          </StyledPaper>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
};

PopoverDateTimeRangePicker.displayName = 'PopoverDateTimeRangePicker';

export default PopoverDateTimeRangePicker;

const StyledPopper = styled(Popper, {
  shouldForwardProp: prop => prop !== 'ownerZIndex',
})<{ ownerZIndex: number }>`
  z-index: ${({ ownerZIndex }) => ownerZIndex};
`;

const StyledPaper = styled('div')(
  ({ theme }) => css`
    padding: 8px;
    border: none;
    background-color: ${theme.color['surface/default']};
    box-shadow: ${theme.elevation['level_2']};
    border-radius: 2px;
  `,
);
